/**
 * Billing and plan limits types
 */

import type { TeamPlan } from '../hooks/useTeams';

/** Types of resources that can be limited */
export type LimitResourceType = 'projects' | 'secrets' | 'members' | 'environments';

/** Usage statistics for a single resource */
export interface ResourceUsage {
  /** Current usage count */
  current: number;
  /** Maximum allowed (-1 for unlimited) */
  limit: number;
  /** Usage percentage (0-100, or 0 if unlimited) */
  percentage: number;
}

/** Complete usage statistics for a team */
export interface TeamUsage {
  team_id: string;
  plan: TeamPlan;
  projects: ResourceUsage;
  secrets: ResourceUsage;
  members: ResourceUsage;
  environments: ResourceUsage;
  /** Resources approaching limits (>80%) */
  warnings: LimitResourceType[];
  /** Resources at their limits */
  at_limit: LimitResourceType[];
}

/** Plan limits configuration */
export interface PlanLimits {
  max_projects: number;
  max_secrets_per_env: number;
  max_members: number;
  max_environments_per_project: number;
}

/** Details when a limit is exceeded (returned with 402 errors) */
export interface LimitExceededDetails {
  resource_type: LimitResourceType;
  current_usage: number;
  limit: number;
  upgrade_required: boolean;
  upgrade_url: string;
}

/** API error response structure */
export interface APIErrorResponse {
  error: string;
  code?: string;
  limit_details?: LimitExceededDetails;
}

/** Billing information for a team */
export interface TeamBilling {
  team_id: string;
  plan: TeamPlan;
  stripe_customer_id?: string;
  subscription_status?: 'active' | 'canceled' | 'past_due' | 'trialing';
  current_period_end?: string;
}

/** Unlimited constant (-1) */
export const UNLIMITED = -1;

/** Check if a limit value represents unlimited */
export function isUnlimited(limit: number): boolean {
  return limit === UNLIMITED;
}

/** Check if usage is near the limit (>80%) */
export function isNearLimit(usage: ResourceUsage): boolean {
  if (isUnlimited(usage.limit)) return false;
  return usage.percentage >= 80;
}

/** Check if usage has reached the limit */
export function isAtLimit(usage: ResourceUsage): boolean {
  if (isUnlimited(usage.limit)) return false;
  return usage.current >= usage.limit;
}

/** Format limit value for display */
export function formatLimit(limit: number): string {
  return isUnlimited(limit) ? 'Unlimited' : limit.toString();
}

/** Get display name for a resource type */
export function getResourceDisplayName(type: LimitResourceType): string {
  const names: Record<LimitResourceType, string> = {
    projects: 'Projects',
    secrets: 'Secrets per Environment',
    members: 'Team Members',
    environments: 'Environments per Project',
  };
  return names[type];
}
